import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { fetchData } from "../../../api/client";
import { CAMPAIGN_TEMPLATES, getTemplateById } from "./campaignTemplates";

const CAMPAIGN_WIZARD_STEPS = ["Objetivo", "Canal", "Template", "Dor e nome"];

const OBJECTIVES = [
  { id: "gerar_leads", label: "Gerar leads" },
  { id: "vender_mais", label: "Vender mais" },
  { id: "recuperar_clientes", label: "Recuperar clientes" },
];

const CHANNELS = [
  { id: "instagram", label: "Instagram" },
  { id: "whatsapp", label: "WhatsApp" },
];

const PAINS_BY_OBJECTIVE = {
  gerar_leads: [
    { id: "baixa_conversao", label: "Baixa conversão" },
    { id: "perda_de_leads", label: "Perda de leads" },
  ],
  vender_mais: [
    { id: "carrinho_abandonado", label: "Carrinho abandonado" },
    { id: "poucas_recompras", label: "Poucas recompras" },
  ],
  recuperar_clientes: [
    { id: "clientes_inativos", label: "Clientes inativos" },
    { id: "sem_followup", label: "Sem follow-up" },
  ],
};

function toFriendlyMessage(err) {
  const raw =
    (err && typeof err === "object" && (err.message || err.errorMessage || err.error)) ||
    (typeof err === "string" ? err : "") ||
    "";
  const status = err?.status ?? err?.response?.status ?? err?.response?.data?.status ?? null;
  if (status >= 500) return "Erro temporário no servidor. Tente novamente em instantes.";
  if (status === 401 || status === 403) return "Você não tem permissão para acessar este recurso.";
  if (status === 404) return "Recurso não encontrado.";
  return raw || "Não foi possível concluir a operação. Tente novamente.";
}

export default function CampaignCreateWizardDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [objective, setObjective] = useState("gerar_leads");
  const [channel, setChannel] = useState("instagram");
  const [templateId, setTemplateId] = useState("funnel_full_generic");
  const [pain, setPain] = useState("baixa_conversao");
  const [name, setName] = useState("Converter comentários do Instagram em vendas");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const pains = useMemo(() => PAINS_BY_OBJECTIVE[objective] || [], [objective]);
  const template = useMemo(() => getTemplateById(templateId), [templateId]);

  const reset = () => {
    setStep(0);
    setObjective("gerar_leads");
    setChannel("instagram");
    setTemplateId("funnel_full_generic");
    setPain("baixa_conversao");
    setName("Converter comentários do Instagram em vendas");
    setLoading(false);
    setErrorMsg("");
  };

  const handleClose = () => {
    onClose?.();
    reset();
  };

  const submit = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchData("/campaigns", "POST", {
        name,
        objective,
        pain,
        segment: "",
        channel,
        channels: [channel],
        templateKey: template?.templateKey || "",
      });
      const id = res?.data?.id;

      // Aplica camada de UX/template pós-criação (sem precisar de backend novo)
      if (id && template?.applyToFlowConfig) {
        try {
          const cRes = await fetchData(`/campaigns/${id}`, "GET");
          const createdCampaign = cRes?.data;
          const flowId = createdCampaign?.flowId;
          if (flowId) {
            const fRes = await fetchData("/flows", "GET");
            const flow = (fRes?.data || []).find((f) => Number(f.id) === Number(flowId)) || null;
            const existingCfg = flow?.config && typeof flow.config === "object" ? flow.config : {};
            const nextCfg = template.applyToFlowConfig(existingCfg);
            await fetchData(`/flows/${flowId}`, "PUT", { config: nextCfg });
          }
        } catch (e) {
          console.warn("Template pós-criação falhou (não bloqueante):", e);
        }
      }

      onCreated?.(id);
      reset();
    } catch (err) {
      console.error("Erro ao criar campanha:", err);
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Criar campanha</DialogTitle>
      <DialogContent>
        {errorMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}

        <Stepper activeStep={step} sx={{ py: 2 }}>
          {CAMPAIGN_WIZARD_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Escolha o objetivo. Isso define a estrutura do funil gerado automaticamente.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Objetivo</InputLabel>
              <Select
                label="Objetivo"
                value={objective}
                onChange={(e) => {
                  const v = String(e.target.value);
                  setObjective(v);
                  const nextPains = PAINS_BY_OBJECTIVE[v] || [];
                  setPain(nextPains[0]?.id || "baixa_conversao");
                }}
              >
                {OBJECTIVES.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        ) : null}

        {step === 1 ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Escolha o canal principal.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select
                label="Canal"
                value={channel}
                onChange={(e) => {
                  const v = String(e.target.value);
                  setChannel(v);
                  if (v === "instagram") setName("Converter comentários do Instagram em vendas");
                  if (v === "whatsapp") setName("Vender mais no WhatsApp");
                }}
              >
                {CHANNELS.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {channel !== "instagram" ? (
              <Alert severity="info">MVP completo está em Instagram. Outros canais entram como template em seguida.</Alert>
            ) : null}
          </Stack>
        ) : null}

        {step === 2 ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Selecione um template. Ele define se sua campanha é um funil completo ou uma automação simples.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select label="Template" value={templateId} onChange={(e) => setTemplateId(String(e.target.value))}>
                {CAMPAIGN_TEMPLATES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.recommended ? "⭐ " : ""}
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {template?.description ? <Alert severity="info">{template.description}</Alert> : null}
          </Stack>
        ) : null}

        {step === 3 ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Escolha a dor e dê um nome claro (orientado a resultado).
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Dor</InputLabel>
              <Select label="Dor" value={pain} onChange={(e) => setPain(String(e.target.value))}>
                {pains.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Nome da campanha" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose}>Cancelar</Button>
        {step > 0 ? <Button onClick={() => setStep((s) => Math.max(0, s - 1))}>Voltar</Button> : null}
        {step < 3 ? (
          <Button variant="contained" onClick={() => setStep((s) => Math.min(3, s + 1))}>
            Continuar
          </Button>
        ) : (
          <Button variant="contained" onClick={submit} disabled={loading || !name.trim()}>
            {loading ? "Criando..." : "Criar campanha"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

